import { errorNotification } from "../errorNotification";
import { invalidateClientSession, isInvalidSessionTrpcError, getInvalidSessionMessage } from "../authSession.util";
import { resolveAppointmentErrorNotification } from "../appointmentErrors.const";
import { EXPERT_ACCOUNT_QUESTIONNAIRE_REQUIRED_CODE } from "@shared/expertAccountQuestionnaire";

const INDIAN_GRIDLINES_REGISTRATION_TYPES = new Set(["GST", "UDM", "CIN"]);

/** Extract user-facing message from a tRPC client error. */
export function getTrpcErrorMessage(error: unknown): string {
  const err = error as {
    error?: { json?: { message?: string } };
    data?: { json?: { message?: string }; code?: string };
    shape?: { message?: string };
    message?: string;
  };
  return (
    err?.error?.json?.message ||
    err?.data?.json?.message ||
    err?.shape?.message ||
    err?.message ||
    "Something went wrong"
  );
}

export function getTrpcErrorCode(error: unknown): string | undefined {
  return (error as { data?: { code?: string } })?.data?.code;
}

export function isExpertAccountQuestionnaireRequiredError(error: unknown): boolean {
  return getTrpcErrorMessage(error) === EXPERT_ACCOUNT_QUESTIONNAIRE_REQUIRED_CODE;
}

/** Wrong GST / Udyam / CIN — show inline in verification modal, not toast + close. */
export function isIndiaGridlinesUserVerificationError(
  error: unknown,
  headquartersCountry: string,
  registrationIdType: string
): boolean {
  if (headquartersCountry.trim() !== "India") {
    return false;
  }
  if (!INDIAN_GRIDLINES_REGISTRATION_TYPES.has(registrationIdType)) {
    return false;
  }
  return getTrpcErrorCode(error) === "BAD_REQUEST";
}

export const handleTrpcError = (error: any) => {
  if (isInvalidSessionTrpcError(error)) {
    invalidateClientSession();
    errorNotification({
      title: "Session ended",
      message: getInvalidSessionMessage(error) || "Please sign in again.",
    });
    return;
  }

  const trpcMessage = getTrpcErrorMessage(error);

  const code = getTrpcErrorCode(error);

  switch (code) {
    case "UNAUTHORIZED":
      if (trpcMessage === "ERR_LOGIN_INVALID_PASSWORD" || trpcMessage === "ERR_LOGIN_INVALID_USER") {
        errorNotification({
          title: "Authentication Failed",
          message: "Invalid email or password",
        });
      } else {
        errorNotification({
          title: "Authentication Failed",
          message: trpcMessage,
        });
      }
      break;

    case "CONFLICT":
      if (trpcMessage === "ERR_SIGN" || trpcMessage === "ERR_SIGNUP_USER_EXISTS") {
        errorNotification({
          title: "Signup Failed",
          message: "User with this email already exists",
        });
      } else {
        const appointmentError = resolveAppointmentErrorNotification(trpcMessage, "Conflict");
        errorNotification({
          title: appointmentError.isAppointmentError ? appointmentError.title : "Conflict",
          message: appointmentError.message,
        });
      }
      break;

    case "FORBIDDEN":
      if (isExpertAccountQuestionnaireRequiredError(error)) {
        break;
      }
      errorNotification({
        title: "Access Denied",
        message: trpcMessage,
      });
      break;

    case "BAD_REQUEST":
      errorNotification({
        title: "Invalid Request",
        message: trpcMessage,
      });
      break;

    case "PRECONDITION_FAILED":
      errorNotification({
        title: "Not ready yet",
        message: trpcMessage,
      });
      break;

    case "TOO_MANY_REQUESTS":
      errorNotification({
        title: "Limit reached",
        message: trpcMessage,
      });
      break;

    case "INTERNAL_SERVER_ERROR":
      errorNotification({
        title: "Something went wrong!",
        message: trpcMessage,
      });
      break;

    default:
      errorNotification({
        title: "Something went wrong!",
        message: trpcMessage,
      });
  }
};
